import { DialogCommonProps } from '../../dialog/types/dialogModel'
import Dialog from '../../../components/dialog/Dialog'
import { Alert, Box, Grid, Snackbar, SnackbarCloseReason } from '@mui/material'
import SvgClose from '../../../icons/SvgClose'
import { gecinaBlueMain, WHITE } from '../../../theme'
import Button from '../../../components/button/Button'
import { buttonTypesEnum } from '../../button/enums/buttonTypesEnum'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { INITIAL_REACTIVATION_PROCEDURE, ReactivationProcedureForm } from './PopinReactivationModel'
import React, { useState } from 'react'
import FormDatePickerField from '../../fields/FormDatePickerField'
import Loader from '../../loader/Loader'
import { champsReactivationEnum } from './popinReactivationEnum'
import { ListeProceduresResponseModel } from '../../../modules/listeProcedures/types/filtresListeProceduresModel'
import { useNavigate } from 'react-router-dom'
import { formatDate_DD_MM_YYYY_DASH } from '../../../utils/formatDate'
import { filtresListeProceduresResponseEnum } from '../../../modules/listeProcedures/enums/listeProceduresEnum'
import { reactivateProcedure } from '../api/procedureArianeApi'
import liens from '../../../enum/liens'

interface AcceptingProps {
	procedure?: ListeProceduresResponseModel
}

type PopinReactivationProps = AcceptingProps & DialogCommonProps

const PopinReactivation: React.FC<PopinReactivationProps> = ({ onCloseDialog, open, procedure }) => {
	const [loading, setLoading] = useState<boolean>(false)
	const [errorMessage, setErrorMessage] = useState<string | undefined>()
	const navigate = useNavigate()
	const methods = useForm<ReactivationProcedureForm>({
		defaultValues: INITIAL_REACTIVATION_PROCEDURE
	})
	const { register, handleSubmit, reset, setError } = methods

	const onSubmit: SubmitHandler<ReactivationProcedureForm> = (values: ReactivationProcedureForm) => {
		//check date expiration
		const dateMinExp = new Date()
		dateMinExp.setDate(dateMinExp.getDate() + 1)
		if (values.expiration_date === null || formatDate_DD_MM_YYYY_DASH(values.expiration_date) <= formatDate_DD_MM_YYYY_DASH(dateMinExp)) {
			setError(champsReactivationEnum.expiration_date, { type: 'custom', message: 'Veuillez saisir une date d\'expiration plus tard que demain' })
		} else {
			if (procedure) {
				setLoading(true)
				return reactivateProcedure({
					signatureRequestId: procedure[filtresListeProceduresResponseEnum.id],
					expiration_date: formatDate_DD_MM_YYYY_DASH(values.expiration_date)
				})
					.then((response) => navigate(liens.detailProcedure, { state: { procedure: response } }))
					.catch(() => setErrorMessage('Une erreur est survenue lors de la réactivation de la procédure'))
					.finally(() => setLoading(false))
			}
		}
	}

	const handleOnClose = () => {
		reset(INITIAL_REACTIVATION_PROCEDURE)
		setLoading(false)
		setErrorMessage(undefined)
		onCloseDialog()
	}

	const handleAlertClose = (
		_event?: React.SyntheticEvent | Event,
		reason?: SnackbarCloseReason
	) => {
		if (reason === 'clickaway') {
			return
		}
		setErrorMessage(undefined)
	}

	return (
		<Dialog
			onCloseDialog={handleOnClose}
			open={open}
			fullScreen={false}
			withPadding={false}
			maxWidth={false}
		>
			<Grid justifyContent="center" sx={{ width: 785, height: 'fit-content', ml: '40px', mr: '40px' }}>
				<Grid container sx={{
					height: 65, mt: '40px', mb: '20px',
					'&::after': {
						top: '110px',
						width: '90%',
						height: '1px',
						content: '\' \'',
						display: 'inline-block',
						background: '#C4C4C4',
						position: 'absolute',
						right: '50px'
					}
				}}>
					<Box sx={{
						width: 681,
						height: 25,
						lineHeight: '25px',
						color: '#122941',
						flex: 'none',
						order: 0,
						flexGrow: 1,
						fontFamily: 'BlackerDisplay', fontSize: 22, fontWeight: 900
					}}>
						Nouvelle date d'expiration
					</Box>
					<Box sx={{
						height: 25,
						flex: 'none',
						order: 1,
						flexGrow: 1,
						cursor: 'pointer'
					}} onClick={onCloseDialog}>
						<SvgClose color={gecinaBlueMain} />
					</Box>
				</Grid>
				<FormProvider {...methods}>
					<form
						onSubmit={handleSubmit(onSubmit)}
					>
						<Grid container item xs={12} sx={{ height: 'fit-content', mt: '20px', mb: '20px' }}>
							<Grid container item xs sx={{ width: '345px', ml: '15px', color: gecinaBlueMain, minHeight: '81px' }}>
								<FormDatePickerField
									register={register(champsReactivationEnum.expiration_date, {
										valueAsDate: true
									})}
									id={champsReactivationEnum.expiration_date}
									label="Date d'expiration*"
									minDate
								/>
							</Grid>
							<Grid container item xs justifyContent="flex-end" sx={{ mt: '10px', ml: 0 }} spacing={2}>
								{loading ?
									<Grid item sx={{ maxWidth: 'max-content', minWidth: 'fit-content' }}>
										<Loader size={30} />
									</Grid>
									:
									<>
										<Grid item sx={{ maxWidth: 'max-content', minWidth: 'fit-content' }}>
											<Button
												sx={{
													height: '51px',
													p: 0,
													width: '195px',
													fontWeight: 600,
													lineHeight: '18.29px'
												}}
												bgcolor={WHITE}
												color={gecinaBlueMain}
												border={true}
												disabled={loading}
												type={buttonTypesEnum.submit}
											>
												Réactiver
											</Button>
										</Grid>
									</>
								}
							</Grid>
						</Grid>
					</form>
				</FormProvider>
				<Snackbar
					anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
					open={!!errorMessage}
					autoHideDuration={3000}
					onClose={handleAlertClose}
				>
					<Alert
						onClose={handleAlertClose}
						severity="error"
						variant="filled"
						sx={{ width: '100%' }}
					>
						{errorMessage}
					</Alert>
				</Snackbar>
			</Grid>
		</Dialog>
	)
}

export default PopinReactivation